// src/components/GroceryList.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../App";
import { aggregateGroceryFromMeals, detectRepeatedMeals } from "../utils/mealPlanGenerator";

export default function GroceryList() {
  const { lockedMeals } = useApp();
  const navigate = useNavigate();

  const [checkedIds, setCheckedIds] = useState(() => getStoredChecked());
  const [filter,     setFilter]     = useState("all"); // "all" | "unchecked"

  // Extract meal objects from locked meals and aggregate ingredients
  const items = useMemo(() => {
    if (!lockedMeals || lockedMeals.length === 0) return [];
    const meals = lockedMeals.map((l) => l.meal).filter(Boolean);
    return aggregateGroceryFromMeals(meals);
  }, [lockedMeals]);

  // Detect repeated & batch-friendly meals among locked selections
  const repeatedMeals = useMemo(() => {
    if (!lockedMeals || lockedMeals.length === 0) return [];
    return detectRepeatedMeals(lockedMeals);
  }, [lockedMeals]);

  // Persist checked state whenever it changes
  useEffect(() => { saveChecked([...checkedIds]); }, [checkedIds]);

  const toggle = (id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const uncheckAll = () => setCheckedIds(new Set());

  const withChecked = items.map((item) => ({
    ...item,
    checked: checkedIds.has(item.id),
  }));

  const displayed = filter === "unchecked"
    ? withChecked.filter((i) => !i.checked)
    : withChecked;

  const checkedCount   = withChecked.filter((i) => i.checked).length;
  const uncheckedCount = withChecked.filter((i) => !i.checked).length;
  const pctDone        = items.length > 0 ? Math.round((checkedCount / items.length) * 100) : 0;

  const lockedCount = lockedMeals ? lockedMeals.length : 0;

  // ── No locked meals yet ─────────────────────────────────────────────────────
  if (lockedCount === 0) {
    return (
      <div className="space-y-5 animate-slide-up">
        <div>
          <h1 className="section-title">Grocery List</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            Ingredients from your locked meals
          </p>
        </div>
        <div className="card text-center py-12 text-slate-500">
          <span className="text-4xl block mb-3">🔒</span>
          <p className="text-sm">No meals locked yet.</p>
          <p className="text-xs mt-1 text-slate-600">
            Lock the meals you want in your Meal Plan — your grocery list will build from those selections.
          </p>
          <button onClick={() => navigate("/meals")}
            className="btn-primary text-sm mt-4 inline-flex items-center gap-1.5">
            <CalendarIcon className="w-4 h-4" />
            Go to Meal Plan
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-slide-up">
      {/* ── Header ────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="section-title">Grocery List</h1>
          <p className="text-slate-500 text-sm mt-0.5">
            {lockedCount} meal{lockedCount !== 1 ? "s" : ""} locked · {items.length} ingredient{items.length !== 1 ? "s" : ""}
          </p>
        </div>
        <button onClick={() => navigate("/meals")} className="btn-ghost text-sm flex items-center gap-1.5">
          <CalendarIcon className="w-4 h-4" />
          Meal Plan
        </button>
      </div>

      {/* ── Repeated / Batch Cook Callouts ────────────────────────────── */}
      {repeatedMeals.length > 0 && (
        <div className="card border-[#ff7c0a]/20 bg-[#ff7c0a]/5">
          <div className="flex items-start gap-3">
            <span className="text-lg flex-shrink-0">🔁</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-[#ff7c0a] mb-2">
                Repeated Meals This Week
              </div>
              <div className="space-y-1.5">
                {repeatedMeals.map((m) => (
                  <div key={m.name} className="flex items-start gap-2 text-xs">
                    <span className="text-slate-300 font-medium flex-1 min-w-0">
                      {m.name}
                    </span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-slate-500">×{m.count}</span>
                      {m.isBatchCook && (
                        <span className="badge-blue">batch-cook</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-slate-500 mt-2">
                Prep these in bulk to save time — one cook session covers multiple meals.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Progress ──────────────────────────────────────────────────── */}
      {items.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-slate-400">
              {checkedCount} of {items.length} items collected
            </span>
            <span className="text-xs font-semibold text-slate-300">{pctDone}%</span>
          </div>
          <div className="progress-track h-1.5">
            <div
              className="progress-fill h-1.5 bg-emerald-500"
              style={{ width: `${pctDone}%` }}
            />
          </div>
          {pctDone === 100 && (
            <div className="text-xs text-emerald-400 mt-2 text-center font-semibold">
              Shopping complete! You're all set for the week.
            </div>
          )}
        </div>
      )}

      {/* ── Filter + clear ────────────────────────────────────────────── */}
      {items.length > 0 && (
        <div className="flex items-center gap-2">
          <div className="flex bg-[#111820] rounded-lg p-1 flex-1">
            {[
              ["all",       `All (${items.length})`],
              ["unchecked", `Remaining (${uncheckedCount})`],
            ].map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)}
                className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-all ${
                  filter === v ? "bg-[#ff7c0a] text-white" : "text-slate-400"
                }`}>
                {l}
              </button>
            ))}
          </div>
          {checkedCount > 0 && (
            <button onClick={uncheckAll} className="btn-ghost text-xs py-1.5 flex-shrink-0">
              Uncheck all
            </button>
          )}
        </div>
      )}

      {/* ── List ──────────────────────────────────────────────────────── */}
      {items.length === 0 ? (
        <div className="card text-center py-12 text-slate-500">
          <span className="text-4xl block mb-3">🛒</span>
          <p className="text-sm">No ingredients found in your locked meals.</p>
          <p className="text-xs mt-1 text-slate-600">
            The meals you locked may not have ingredient data (e.g. custom saved meals).
          </p>
        </div>
      ) : displayed.length === 0 ? (
        <div className="card text-center py-8 text-slate-500">
          <span className="text-2xl block mb-2">✅</span>
          <p className="text-sm">All items collected!</p>
        </div>
      ) : (
        <div className="space-y-1.5">
          {displayed.map((item) => (
            <button
              key={item.id}
              onClick={() => toggle(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border text-left
                          transition-all duration-150 ${
                            item.checked
                              ? "bg-[#0d1117] border-[#1f2a3c] opacity-50"
                              : "bg-[#161d28] border-[#1f2a3c] hover:border-[#ff7c0a]/30"
                          }`}
            >
              {/* Checkbox */}
              <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0
                               border-2 transition-all ${
                                 item.checked
                                   ? "bg-emerald-500 border-emerald-500"
                                   : "border-[#334155] bg-transparent"
                               }`}>
                {item.checked && (
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"/>
                  </svg>
                )}
              </div>

              {/* Item info */}
              <div className="flex-1 min-w-0">
                <div className={`text-sm font-medium transition-all ${
                  item.checked ? "line-through text-slate-600" : "text-slate-200"
                }`}>
                  {item.name}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{item.quantity}</div>
              </div>

              {/* Source meals */}
              <div className="text-xs text-slate-600 text-right flex-shrink-0 max-w-32 hidden sm:block">
                {item.mealSources.length > 1 ? (
                  <span className="text-[#ff7c0a]/70">{item.mealSources.length} meals</span>
                ) : (
                  <span className="truncate block">{item.mealName}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* ── Note ──────────────────────────────────────────────────────── */}
      {items.length > 0 && (
        <div className="text-xs text-slate-600 text-center px-4">
          Ingredients are deduped across locked meals. Tap any item to mark it collected.
        </div>
      )}
    </div>
  );
}

// ─── Icons ───────────────────────────────────────────────────────────────────

function CalendarIcon({ className }) {
  return <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
  </svg>;
}

// ─── Session storage helpers (ephemeral UI state) ────────────────────────────

function getStoredChecked() {
  try {
    const raw = sessionStorage.getItem("grocery_checked");
    return new Set(raw ? JSON.parse(raw) : []);
  } catch { return new Set(); }
}

function saveChecked(ids) {
  try { sessionStorage.setItem("grocery_checked", JSON.stringify(ids)); }
  catch {}
}
